// components/AdminManagement.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/appIcon';
import Button from 'components/ui/button';
import Input from 'components/ui/input';
import Modal from 'components/ui/modal';
import Table from 'components/ui/table';
import AdminNavigation from 'layouts/adminNavigation';
import { useAuth } from 'components/ui/navigationGuard';
import Notification from 'components/ui/notification';
import Select from 'components/ui/select';
import useAdminManagementLogic from 'hooks/useAdmin';

const AdminManagement = () => {
  const { user, isAuthenticated, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  
  // Use the custom hook to get all the state and functions
  const {
    isNavCollapsed,
    admins,
    roles,
    loading,
    modalState,
    notification,
    selectedAdmins,
    showBulkActions,
    formData,
    errors,
    columns,
    setIsNavCollapsed,
    setModalState,
    setSelectedAdmins,
    setShowBulkActions,
    closeNotification,
    showNotification,
    loadAdmins,
    handleInputChange,
    validateForm,
    handleSave,
    handleAddAdmin,
    handleEditAdmin,
    handleDeleteAdmin,
    handleToggleStatus,
    handleToggleSuperAdmin,
    handleBulkAction,
    handleExportCSV,
    handleSelectAdmin,
    handleSelectAllAdmins
  } = useAdminManagementLogic(isSuperAdmin);

  if (!isSuperAdmin) {
    return (
      <>
        <AdminNavigation
          user={user}
          isCollapsed={isNavCollapsed}
          onToggleCollapse={() => setIsNavCollapsed(!isNavCollapsed)}
        />
        <div className={`min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 transition-all duration-200 ${isNavCollapsed ? 'lg:ml-16' : 'lg:ml-64'} pt-16`}>
          <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Icon name="ShieldX" className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Access denied</h3>
              <p className="mt-1 text-sm text-gray-500">
                You don't have permission to access admin management.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <AdminNavigation
          user={user}
          isCollapsed={isNavCollapsed}
          onToggleCollapse={() => setIsNavCollapsed(!isNavCollapsed)}
        />
        <div className={`min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 transition-all duration-200 ${isNavCollapsed ? 'lg:ml-16' : 'lg:ml-64'} pt-16`}>
          <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-muted-foreground">Loading admins...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminNavigation
        user={user}
        isCollapsed={isNavCollapsed}
        onToggleCollapse={() => setIsNavCollapsed(!isNavCollapsed)}
      />
      <div className={`min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 transition-all duration-200 ${isNavCollapsed ? 'lg:ml-16' : 'lg:ml-64'} pt-16`}>
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Admin Management</h1>
              <p className="text-slate-500 mt-1">Manage system administrators</p>
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-2">
              <Button
                variant="outline"
                onClick={handleExportCSV}
                className="px-4 py-2 rounded-lg"
              >
                <Icon name="Download" size={16} className="mr-2" />
                Export CSV
              </Button>
              <Button
                variant="default"
                onClick={handleAddAdmin}
                className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Icon name="Plus" size={16} className="mr-2" />
                Add Admin
              </Button>
            </div>
          </div>

          {showBulkActions && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
              <span className="text-sm text-blue-800">
                {selectedAdmins.length} admin{selectedAdmins.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkAction("activate")}
                  className="text-green-600 hover:text-green-700"
                >
                  <Icon name="UserCheck" size={14} className="mr-1" />
                  Activate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkAction("deactivate")}
                  className="text-yellow-600 hover:text-yellow-700"
                >
                  <Icon name="UserX" size={14} className="mr-1" />
                  Deactivate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkAction("delete")}
                  className="text-red-600 hover:text-red-700"
                >
                  <Icon name="Trash" size={14} className="mr-1" />
                  Delete
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedAdmins([]);
                    setShowBulkActions(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <Table
              columns={columns}
              data={admins}
              emptyMessage="No admins found"
            />
          </div>
        </div>
      </div>

      {/* Admin Modal */}
      <Modal isOpen={modalState.isOpen} onClose={() => setModalState({ isOpen: false, admin: null, mode: "add" })}>
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">
            {modalState.mode === "add" ? "Add New Admin" : "Edit Admin"}
          </h2>
          
          <div className="space-y-4">
            <Input
              label="Admin Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Enter admin name"
              required
              error={errors.name}
            />
            
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="Enter email address"
              required
              error={errors.email}
            />
            
            <Input
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              placeholder={modalState.mode === "add" ? "Enter password" : "Enter new password (leave blank to keep current)"}
              required={modalState.mode === "add"}
              error={errors.password}
            />
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Role
              </label>
              <Select
                value={formData.role_id}
                onChange={(e) => handleInputChange("role_id", e.target.value)}
                className="w-full"
                error={errors.role_id}
              >
                <option value="">Select a role</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Select>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => handleInputChange("is_active", e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-white border-slate-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_active" className="ml-2 text-sm text-foreground">
                Active
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_super_admin"
                checked={formData.is_super_admin}
                onChange={(e) => handleInputChange("is_super_admin", e.target.checked)}
                className="w-4 h-4 text-purple-600 bg-white border-slate-300 rounded focus:ring-purple-500"
              />
              <label htmlFor="is_super_admin" className="ml-2 text-sm text-foreground">
                Super Admin
              </label>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setModalState({ isOpen: false, admin: null, mode: "add" })}
            >
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {modalState.mode === "add" ? "Add Admin" : "Save Changes"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Notification Component */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      )}
    </>
  );
};

export default AdminManagement;